/**
 * The Vault: past days, played after today's puzzle (docs/BUILD_BRIEF_VAULT.md, DECISIONS "The Vault").
 * Pure functions over the bundled date index (`DayRef[]`: set, number, date — no puzzle content), so
 * the rules are easy to test. Thresholds live here and nowhere else.
 */
import { ROUND_ORDER, type DayRef, type PuzzleSet } from "../puzzles/types";

/** The Vault entry appears once a set has at least this many past days. */
export const VAULT_MIN_PAST_DAYS = 3;
/** How many past days are open (the free/paid boundary, decided later). */
export const FREE_PAST_DAYS = Infinity;

/** How a day is being played. `mix` (Theme mix) comes with Phase C. */
export type Mode = "daily" | "vault";

/** "adult-003" from { set: "adult", number: 3 }. */
export const dayIdOf = (r: { set: PuzzleSet; number: number }): string => `${r.set}-${String(r.number).padStart(3, "0")}`;

const newestFirst = (a: DayRef, b: DayRef): number => (a.date < b.date ? 1 : a.date > b.date ? -1 : b.number - a.number);

/** Today's puzzle: the latest scheduled day of the set whose date has started for this player. */
export function dailyRef(refs: readonly DayRef[], set: PuzzleSet, today: string): DayRef | null {
  return refs.filter((r) => r.set === set && r.date <= today).sort(newestFirst)[0] ?? null;
}

/** Past days: scheduled days before today's puzzle, newest first. Today's puzzle is never in the Vault. */
export function vaultRefs(refs: readonly DayRef[], set: PuzzleSet, today: string): DayRef[] {
  const daily = dailyRef(refs, set, today);
  if (!daily) return [];
  return refs.filter((r) => r.set === set && r.date < daily.date).sort(newestFirst).slice(0, FREE_PAST_DAYS);
}

/** A day dated before today's puzzle is played in the Vault; today's (and, in playtest builds, future or undated days) is the daily. */
export const modeOf = (date: string | undefined, dailyDate: string | null): Mode => (date && dailyDate && date < dailyDate ? "vault" : "daily");

export type Gate = "hidden" | "locked" | "open";

/**
 * The Vault button: hidden until there are enough past days; locked until today's puzzle is finished
 * at this level; open once it is (or when there's no puzzle today).
 */
export function vaultGate(pastDays: number, dailyExists: boolean, dailyDone: boolean): Gate {
  if (pastDays < VAULT_MIN_PAST_DAYS) return "hidden";
  return !dailyExists || dailyDone ? "open" : "locked";
}

/**
 * Saved results still valid for today's rules: each has a `max` (the points change of 29 Sep) and the round type
 * the day has now (Pin it, Next stop, Whose steps? since 30 Sep). Anything else was played under old rules, so that
 * day starts afresh.
 */
export const currentResults = (results: readonly ({ type?: string; max?: number } | null)[]): boolean =>
  results.every((r, i) => !r || (r.max != null && (r.type == null || r.type === ROUND_ORDER[i])));

/** The saved part of a day that the Vault list needs. */
export interface SavedDay {
  results: ({ type?: string; score: number; max?: number } | null)[];
  mode?: Mode;
  playedOn?: string;
}

export interface VaultRow {
  ref: DayRef;
  id: string;
  status: "done" | "partial" | "new";
  /** Rounds played (0–3). */
  played: number;
  score: number;
  max: number;
}

/** One row per past day. Results saved under older rules (see `currentResults`) count as unplayed, as in the game. */
export function vaultRows(refs: readonly DayRef[], saved: (id: string) => SavedDay | null): VaultRow[] {
  return refs.map((ref) => {
    const id = dayIdOf(ref);
    const s = saved(id);
    const results = s && currentResults(s.results) ? s.results.filter((r): r is { score: number; max: number } => !!r) : [];
    const played = Math.min(3, results.length);
    return {
      ref,
      id,
      status: played >= 3 ? "done" : played > 0 ? "partial" : "new",
      played,
      score: results.reduce((a, r) => a + r.score, 0),
      max: results.reduce((a, r) => a + r.max, 0),
    };
  });
}

/** The next day to offer after `current`: the first not finished, going down the list (newest first) and wrapping round. */
export function nextUnplayed(rows: readonly VaultRow[], current: number): VaultRow | null {
  const at = rows.findIndex((r) => r.ref.number === current);
  for (let i = 1; i <= rows.length; i++) {
    const r = rows[(at + i + rows.length) % rows.length]!;
    if (r.status !== "done" && r.ref.number !== current) return r;
  }
  return null;
}

/** The date a finish adds to the played streak: today for the daily, none for the Vault. */
export const streakDate = (mode: Mode, today: string): string | null => (mode === "daily" ? today : null);

const addDays = (date: string, n: number): string => new Date(Date.parse(`${date}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Played in the Vault within the last 7 days (today included) — the `v7` analytics flag. */
export const vault7 = (last: string | null, today: string): 0 | 1 => (last && /^\d{4}-\d{2}-\d{2}$/.test(last) && last > addDays(today, -7) && last <= today ? 1 : 0);

/** The date of the next scheduled day of this set after `today`, or null (the countdown shows only when there is one). */
export function nextDayDate(refs: readonly DayRef[], set: PuzzleSet, today: string): string | null {
  return refs.filter((r) => r.set === set && r.date > today).reduce<string | null>((a, r) => (a == null || r.date < a ? r.date : a), null);
}
